// CR075: Delete Batch Run Dialog - Soft Delete and Hard Purge
// CR158: Updated to check for stale steps instead of just RUNNING status
import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Trash2, Archive, AlertTriangle, Loader2, ShieldAlert, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { hasActiveNonStaleSteps, hasStaleRunningSteps } from "./RunStatusDebugHeader";

interface BatchRunStep {
  id: string;
  step_key: string;
  step_name: string;
  status: string;
  updated_at?: string;
}

interface DeleteBatchRunDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchRunId: string;
  status: string;
  publishedCount: number;
  onDeleted: () => void;
  steps?: BatchRunStep[];
  onShowCancelDialog?: () => void;
}

interface RecordCounts {
  staging_items: number;
  normalized_deals: number;
  deduped_deals: number;
  batch_run_steps: number;
}

export function DeleteBatchRunDialog({
  open,
  onOpenChange,
  batchRunId,
  status,
  publishedCount,
  onDeleted,
  steps = [],
  onShowCancelDialog,
}: DeleteBatchRunDialogProps) {
  const { toast } = useToast();
  const [deleteType, setDeleteType] = useState<"SOFT" | "PURGE">("SOFT");
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [purgeEnabled, setPurgeEnabled] = useState(false);
  const [counts, setCounts] = useState<RecordCounts | null>(null);
  const [loadingCounts, setLoadingCounts] = useState(false);

  const expectedConfirm = deleteType === "SOFT" ? "DELETE" : "PURGE";
  const canConfirm = confirmText === expectedConfirm;
  
  // CR158: Check for active non-stale steps instead of just RUNNING status
  const isActivelyRunning = status === "RUNNING" && hasActiveNonStaleSteps(steps);
  const hasStaleSteps = status === "RUNNING" && hasStaleRunningSteps(steps);

  // Check if purge is enabled
  useEffect(() => {
    const checkPurgeFlag = async () => {
      const { data } = await supabase
        .from("feature_flags")
        .select("enabled")
        .eq("flag_name", "ff_batch_runs_purge_enabled")
        .single();
      setPurgeEnabled(data?.enabled ?? false);
    };
    if (open) {
      checkPurgeFlag();
    }
  }, [open]);

  // Fetch counts when dialog opens or tab changes to PURGE
  useEffect(() => {
    const fetchCounts = async () => {
      if (!open || deleteType !== "PURGE") return;
      setLoadingCounts(true);
      try {
        const [stagingResult, normalizedResult, dedupedResult, stepsResult] = await Promise.all([
          supabase
            .from("cr075_staging_deal_items")
            .select("*", { count: "exact", head: true })
            .eq("batch_run_id", batchRunId),
          supabase
            .from("cr075_staging_deals_normalized")
            .select("*", { count: "exact", head: true })
            .eq("batch_run_id", batchRunId),
          supabase
            .from("cr075_staging_deals_deduped")
            .select("*", { count: "exact", head: true })
            .eq("batch_run_id", batchRunId),
          supabase
            .from("cr075_batch_run_steps")
            .select("*", { count: "exact", head: true })
            .eq("batch_run_id", batchRunId),
        ]);

        setCounts({
          staging_items: stagingResult.count || 0,
          normalized_deals: normalizedResult.count || 0,
          deduped_deals: dedupedResult.count || 0,
          batch_run_steps: stepsResult.count || 0,
        });
      } catch (err) {
        console.error("Failed to fetch counts:", err);
      } finally {
        setLoadingCounts(false);
      }
    };
    fetchCounts();
  }, [open, deleteType, batchRunId]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setConfirmText("");
      setReason("");
      setDeleteType("SOFT");
      setCounts(null);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!canConfirm || isDeleting) return;

    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-batch-run", {
        body: {
          batch_run_id: batchRunId,
          delete_type: deleteType,
          reason: reason || undefined,
        },
      });

      if (error) throw error;

      toast({
        title: deleteType === "SOFT" ? "Batch run archived" : "Batch run purged",
        description:
          deleteType === "SOFT"
            ? "The batch run has been hidden from the list."
            : `Deleted ${data.counts?.staging_items || 0} staging items, ${data.counts?.normalized_deals || 0} normalized, ${data.counts?.deduped_deals || 0} deduped.`,
      });

      onOpenChange(false);
      onDeleted();
    } catch (err) {
      console.error("Failed to delete batch run:", err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to delete batch run",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            Delete Batch Run
          </AlertDialogTitle>
          <AlertDialogDescription>
            Run ID: <code className="text-xs">{batchRunId.slice(0, 8)}</code>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* CR158: Show different alerts based on stale vs active running */}
        {isActivelyRunning && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Run is still executing</AlertTitle>
            <AlertDescription>
              Cancel the run before deleting.
            </AlertDescription>
          </Alert>
        )}

        {hasStaleSteps && !isActivelyRunning && (
          <Alert className="border-orange-500/50 bg-orange-500/10">
            <Zap className="h-4 w-4 text-orange-500" />
            <AlertTitle className="text-orange-600">Stale steps detected</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>This run has steps that haven't updated in 10+ minutes and may be stuck.</p>
              {onShowCancelDialog && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    onOpenChange(false);
                    onShowCancelDialog();
                  }}
                  className="mt-2"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Force Stop Stale Steps
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {!isActivelyRunning && !hasStaleSteps && (
          <Tabs value={deleteType} onValueChange={(v) => setDeleteType(v as "SOFT" | "PURGE")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="SOFT" className="gap-2">
                <Archive className="h-4 w-4" />
                Archive
              </TabsTrigger>
              <TabsTrigger value="PURGE" disabled={!purgeEnabled} className="gap-2">
                <ShieldAlert className="h-4 w-4" />
                Purge
                {!purgeEnabled && (
                  <Badge variant="secondary" className="ml-1 text-xs">
                    Disabled
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="SOFT" className="space-y-4 mt-4">
              <Alert>
                <Archive className="h-4 w-4" />
                <AlertTitle>Archive (Soft Delete)</AlertTitle>
                <AlertDescription>
                  Removes this run from Recent Batch Runs. All data is retained for audit.
                  Use "Show deleted" toggle to view archived runs.
                </AlertDescription>
              </Alert>
            </TabsContent>

            <TabsContent value="PURGE" className="space-y-4 mt-4">
              <Alert variant="destructive">
                <ShieldAlert className="h-4 w-4" />
                <AlertTitle>Permanent Delete (Purge)</AlertTitle>
                <AlertDescription>
                  This will permanently delete all run-scoped data. This cannot be undone.
                </AlertDescription>
              </Alert>

              {publishedCount > 0 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Published deals preserved</AlertTitle>
                  <AlertDescription>
                    {publishedCount} deals were published from this run. Purge will NOT remove
                    production or archive entries.
                  </AlertDescription>
                </Alert>
              )}

              <div className="rounded-lg border p-4 space-y-2">
                <div className="text-sm font-medium">Records to be deleted:</div>
                {loadingCounts ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading counts...
                  </div>
                ) : counts ? (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Staging items: <strong>{counts.staging_items}</strong></div>
                    <div>Normalized: <strong>{counts.normalized_deals}</strong></div>
                    <div>Deduped: <strong>{counts.deduped_deals}</strong></div>
                    <div>Steps: <strong>{counts.batch_run_steps}</strong></div>
                  </div>
                ) : null}
              </div>
            </TabsContent>

            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="reason">Reason (optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="Why are you deleting this run?"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm">
                  Type <code className="px-1 py-0.5 bg-muted rounded">{expectedConfirm}</code> to confirm
                </Label>
                <Input
                  id="confirm"
                  placeholder={expectedConfirm}
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  autoComplete="off"
                />
              </div>
            </div>
          </Tabs>
        )}

        <AlertDialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!canConfirm || isDeleting || isActivelyRunning}
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {deleteType === "SOFT" ? "Archiving..." : "Purging..."}
              </>
            ) : deleteType === "SOFT" ? (
              "Archive Run"
            ) : (
              "Purge Run"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
